import type { ApiExportRequest, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';

import type { BoxClearSimulatedItemView, MachinesCashLog } from '@/types';
import type { BoxClearAuditLogView, BoxClearDetailView } from '@/types/generated/athena';
import type {
  BoxClearSimulateCreateRequest,
  ConfirmBoxClearRequest,
  CreateBoxClearRequest,
  InvalidateBoxClearRequest,
} from '@/types/generated/boro';
import type { CashLogStatus } from '@/types/machine';

/**
 * Machines Cash Logs RTK-Query API
 *
 * Reads live on athena (`v2/boxClears*`); previews and write commands live on
 * boro (`v2/CashBox*`). See DEV-1610 (athena read/audit) and DEV-1608
 * (boro pointer command endpoints).
 *
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
export const cashLogsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ----- athena: reads -----
    getCashLogs: build.query<ApiResponse<MachinesCashLog[]>, ApiListingRequest>({
      query: (data) => athena({ url: 'v2/boxClears', method: 'POST', data }),
      providesTags: (response) => {
        const result = response?.data || [];
        return [
          ...result.map(({ id }) => ({ type: 'MachinesCashLogs', id }) as const),
          { type: 'MachinesCashLogs' as const, id: 'LIST' },
        ];
      },
    }),
    getCashLogsExport: build.mutation<BlobPart, ApiExportRequest>({
      query: ({ type, data }) =>
        athena({
          url: `v2/boxClears/${type}`,
          method: 'POST',
          data,
        }),
    }),
    /** Single cash clear detail — includes its assigned transactions. */
    getCashLog: build.query<ApiResponse<BoxClearDetailView>, string>({
      query: (id) => athena({ url: `v2/boxClears/${id}` }),
      providesTags: (_response, _error, id) => [{ type: 'MachinesCashLogs', id }],
    }),
    /** Audit history for a single cash clear (paginated). */
    getCashLogAuditLogs: build.query<
      ApiResponse<BoxClearAuditLogView[]>,
      { id: string; data?: ApiListingRequest }
    >({
      query: ({ id, data }) =>
        athena({ url: `v2/boxClears/${id}/auditlogs`, method: 'POST', data: data ?? {} }),
      providesTags: (_response, _error, { id }) => [
        { type: 'MachinesCashLogs', id: `${id}-AUDIT` },
      ],
    }),
    /** Operator-wide cash clear audit log (paginated). */
    getAllCashLogAuditLogs: build.query<
      ApiResponse<BoxClearAuditLogView[]>,
      ApiListingRequest | void
    >({
      query: (data) => athena({ url: 'v2/boxClears/auditlogs', method: 'POST', data: data ?? {} }),
      providesTags: [{ type: 'MachinesCashLogs', id: 'AUDIT-ALL' }],
    }),

    // ----- boro: simulate previews (no persistence) -----
    /**
     * Placement preview for a new manual clear. Called live as the operator
     * edits machine / date / time in the Create Manual Clear modal.
     * Returns the affected window: previous / new (`cash_clear_id === 'NEW'`) /
     * next clears with original and simulated totals.
     */
    simulateCreateClear: build.query<
      ApiResponse<BoxClearSimulatedItemView[]>,
      BoxClearSimulateCreateRequest
    >({
      query: (data) => boro({ url: 'v2/CashBox/simulate', method: 'POST', data }),
    }),
    /**
     * Rebalance preview for invalidating a clear. Returns the affected clears
     * (the one being invalidated + the adjacent absorbing clear) with original
     * and simulated totals.
     */
    simulateInvalidateClear: build.query<ApiResponse<BoxClearSimulatedItemView[]>, number>({
      query: (id) => boro({ url: `v2/CashBox/${id}/simulate`, method: 'DELETE' }),
    }),

    // ----- boro: write commands -----
    /** Confirm a provisional clear. No reason required. */
    confirmCashLog: build.mutation<void, { id: number; reason?: string }>({
      query: ({ id, reason }) =>
        boro({ url: `v2/CashBox/${id}/confirm`, method: 'PATCH', data: { reason } }),
      invalidatesTags: (_response, _error, { id }) => [
        { type: 'MachinesCashLogs', id },
        { type: 'MachinesCashLogs', id: 'LIST' },
        { type: 'MachinesCashLogs', id: `${id}-AUDIT` },
        { type: 'MachinesCashLogs', id: 'AUDIT-ALL' },
      ],
    }),
    /** Invalidate a phantom clear. Reason is required and written to the audit log. */
    invalidateCashLog: build.mutation<void, { id: number } & InvalidateBoxClearRequest>({
      query: ({ id, reason }) =>
        boro({ url: `v2/CashBox/${id}/invalidate`, method: 'PATCH', data: { reason } }),
      invalidatesTags: (_response, _error, { id }) => [
        { type: 'MachinesCashLogs', id },
        { type: 'MachinesCashLogs', id: 'LIST' },
        { type: 'MachinesCashLogs', id: `${id}-AUDIT` },
        { type: 'MachinesCashLogs', id: 'AUDIT-ALL' },
      ],
    }),
    /**
     * Revert a confirmed clear back to provisional via the generic
     * `PATCH v2/CashBox/{id}` update — boro has no dedicated unconfirm route.
     * The optional `reason` is written to the audit log.
     */
    setCashLogStatus: build.mutation<void, { id: number; status: CashLogStatus; reason?: string }>({
      query: ({ id, status, reason }) =>
        boro({
          url: `v2/CashBox/${id}`,
          method: 'PATCH',
          data: { status, reason },
        }),
      invalidatesTags: (_response, _error, { id }) => [
        { type: 'MachinesCashLogs', id },
        { type: 'MachinesCashLogs', id: 'LIST' },
        { type: 'MachinesCashLogs', id: `${id}-AUDIT` },
        { type: 'MachinesCashLogs', id: 'AUDIT-ALL' },
      ],
    }),
    /** Create a manual clear (boundary). Reason is required. */
    createCashLog: build.mutation<void, CreateBoxClearRequest>({
      query: (data) => boro({ url: 'v2/CashBox', method: 'POST', data }),
      invalidatesTags: [
        { type: 'MachinesCashLogs', id: 'LIST' },
        { type: 'MachinesCashLogs', id: 'AUDIT-ALL' },
      ],
    }),
    /**
     * Add an operator note to a clear (boro `box_clears:add_note`). Invalidates
     * the clear detail so the new note appears, plus the audit tags since notes
     * surface in the operator-wide audit history.
     */
    addCashLogNote: build.mutation<void, { id: number; body: string; author: string }>({
      query: ({ id, body, author }) =>
        boro({ url: `v2/CashBox/${id}/notes`, method: 'POST', data: { note_text: body, author } }),
      invalidatesTags: (_response, _error, { id }) => [
        { type: 'MachinesCashLogs', id },
        { type: 'MachinesCashLogs', id: `${id}-AUDIT` },
        { type: 'MachinesCashLogs', id: 'AUDIT-ALL' },
      ],
    }),
  }),
});

export const {
  useGetCashLogsQuery,
  useGetCashLogsExportMutation,
  useGetCashLogQuery,
  useGetCashLogAuditLogsQuery,
  useGetAllCashLogAuditLogsQuery,
  useSimulateCreateClearQuery,
  useSimulateInvalidateClearQuery,
  useConfirmCashLogMutation,
  useInvalidateCashLogMutation,
  useSetCashLogStatusMutation,
  useCreateCashLogMutation,
  useAddCashLogNoteMutation,
} = cashLogsApi;

export const {
  endpoints: {
    getCashLogs,
    getCashLogsExport,
    getCashLog,
    getCashLogAuditLogs,
    getAllCashLogAuditLogs,
    simulateCreateClear,
    simulateInvalidateClear,
    confirmCashLog,
    invalidateCashLog,
    setCashLogStatus,
    createCashLog,
    addCashLogNote,
  },
} = cashLogsApi;

// `ConfirmBoxClearRequest` is re-exported for callers that build the (optional)
// confirm payload explicitly.
export type { ConfirmBoxClearRequest };
